// Types + helpers for the Promo Code admin page (promo_master).
// See docs/PROMO_GATEWAY_SPEC.md for the API contract.

export type DiscountType = 'amount' | 'percentage';

export interface Promo {
  promo_id: string;
  promo_code: string;
  title: string | null;
  description: string | null;
  discount_type: DiscountType;
  discount_value: string; // decimal serialized as string, e.g. "50.00"
  min_price: string | null;
  max_discount: string | null;
  start_date: string; // ISO-8601 in responses
  end_date: string;
  usage_limit: number;
  per_user_usage_count: number;
  is_active: number; // 0 | 1
  promo_usage_count: number;
  created_by: string | null;
  modified_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PromoEnvelope<T = unknown> {
  status: number;
  message: string;
  data: T;
}

// Controlled-form shape — every field a string/bool so inputs stay controlled.
export interface PromoFormValues {
  promo_code: string;
  title: string;
  description: string;
  discount_type: DiscountType | '';
  discount_value: string;
  min_price: string;
  max_discount: string;
  start_date: string; // datetime-local value "YYYY-MM-DDTHH:mm"
  end_date: string;
  usage_limit: string;
  per_user_usage_count: string;
  is_active: boolean;
}

export type FieldErrors = Partial<Record<keyof PromoFormValues, string>>;

export const emptyPromoForm = (): PromoFormValues => ({
  promo_code: '',
  title: '',
  description: '',
  discount_type: '',
  discount_value: '',
  min_price: '',
  max_discount: '',
  start_date: '',
  end_date: '',
  usage_limit: '',
  per_user_usage_count: '',
  is_active: true,
});

// ---- date helpers ---------------------------------------------------------
// API/ISO ("2026-01-01T00:00:00.000Z" or "2026-01-01 00:00:00") -> datetime-local
export const toLocalInput = (v?: string | null): string => {
  if (!v) return '';
  return v.replace(' ', 'T').slice(0, 16);
};

// datetime-local ("YYYY-MM-DDTHH:mm") -> API ("YYYY-MM-DD HH:MM:SS")
export const toApiDateTime = (v: string): string => {
  if (!v) return '';
  const s = v.replace('T', ' ');
  return s.length === 16 ? `${s}:00` : s;
};

// ---- display helpers ------------------------------------------------------
const toNum = (v: string | number | null | undefined): number =>
  v === null || v === undefined || v === '' ? NaN : Number(v);

export const formatDiscount = (p: Promo): string => {
  const v = toNum(p.discount_value);
  if (Number.isNaN(v)) return '—';
  return p.discount_type === 'percentage' ? `${v}%` : `$${v.toFixed(2)}`;
};

export const formatMoney = (v: string | number | null | undefined): string => {
  const n = toNum(v);
  return Number.isNaN(n) ? '—' : `$${n.toFixed(2)}`;
};

export const formatDate = (v?: string | null): string => {
  if (!v) return '—';
  return v.replace('T', ' ').replace(/\.\d+/, '').replace('Z', '').slice(0, 16);
};

export const formatDateRange = (p: Promo): string =>
  `${formatDate(p.start_date)} → ${formatDate(p.end_date)}`;

export const promoToForm = (p: Promo): PromoFormValues => ({
  promo_code: p.promo_code ?? '',
  title: p.title ?? '',
  description: p.description ?? '',
  discount_type: (p.discount_type as DiscountType) || '',
  discount_value: p.discount_value != null ? String(Number(p.discount_value)) : '',
  min_price: p.min_price != null ? String(Number(p.min_price)) : '',
  max_discount: p.max_discount != null ? String(Number(p.max_discount)) : '',
  start_date: toLocalInput(p.start_date),
  end_date: toLocalInput(p.end_date),
  usage_limit: p.usage_limit != null ? String(p.usage_limit) : '',
  per_user_usage_count: p.per_user_usage_count != null ? String(p.per_user_usage_count) : '',
  is_active: Number(p.is_active) === 1,
});

// ---- validation (mirrors the API rules; server re-validates) ---------------
const CODE_RE = /^[A-Za-z0-9_-]+$/;

export const validatePromoForm = (
  v: PromoFormValues,
  mode: 'create' | 'edit'
): FieldErrors => {
  const e: FieldErrors = {};

  if (mode === 'create') {
    if (!v.promo_code.trim()) e.promo_code = 'Promo code is required';
    else if (v.promo_code.length > 50) e.promo_code = 'Max 50 characters';
    else if (!CODE_RE.test(v.promo_code)) e.promo_code = 'Only letters, numbers, _ and -';
  }

  if (!v.discount_type) e.discount_type = 'Select a discount type';

  const dv = Number(v.discount_value);
  if (v.discount_value === '' || Number.isNaN(dv)) e.discount_value = 'Discount value is required';
  else if (dv <= 0) e.discount_value = 'Must be greater than 0';
  else if (v.discount_type === 'percentage' && dv > 100) e.discount_value = 'Percentage cannot exceed 100';

  if (v.title && v.title.length > 36) e.title = 'Max 36 characters';
  if (v.description && v.description.length > 255) e.description = 'Max 255 characters';

  const nonNeg = (s: string) => {
    const n = Number(s);
    return Number.isNaN(n) || n < 0;
  };
  const nonNegInt = (s: string) => {
    const n = Number(s);
    return !Number.isInteger(n) || n < 0;
  };
  if (v.min_price !== '' && nonNeg(v.min_price)) e.min_price = 'Must be 0 or more';
  if (v.max_discount !== '' && nonNeg(v.max_discount)) e.max_discount = 'Must be 0 or more';
  if (v.usage_limit !== '' && nonNegInt(v.usage_limit)) e.usage_limit = 'Whole number ≥ 0';
  if (v.per_user_usage_count !== '' && nonNegInt(v.per_user_usage_count))
    e.per_user_usage_count = 'Whole number ≥ 0';

  if (!v.start_date) e.start_date = 'Start date is required';
  if (!v.end_date) e.end_date = 'End date is required';
  // datetime-local strings are fixed-width, so lexical compare == chronological.
  if (v.start_date && v.end_date && v.end_date <= v.start_date)
    e.end_date = 'End must be after start';

  return e;
};

export const buildCreatePayload = (v: PromoFormValues, createdBy: string) => {
  const p: Record<string, unknown> = {
    promo_code: v.promo_code.trim().toUpperCase(),
    discount_type: v.discount_type,
    discount_value: Number(v.discount_value),
    start_date: toApiDateTime(v.start_date),
    end_date: toApiDateTime(v.end_date),
    is_active: v.is_active ? 1 : 0,
    created_by: createdBy,
  };
  if (v.title.trim()) p.title = v.title.trim();
  if (v.description.trim()) p.description = v.description.trim();
  if (v.min_price !== '') p.min_price = Number(v.min_price);
  if (v.max_discount !== '') p.max_discount = Number(v.max_discount);
  if (v.usage_limit !== '') p.usage_limit = Number(v.usage_limit);
  if (v.per_user_usage_count !== '') p.per_user_usage_count = Number(v.per_user_usage_count);
  return p;
};

export const buildUpdatePayload = (v: PromoFormValues, modifiedBy: string) => ({
  title: v.title.trim(),
  description: v.description.trim(),
  discount_type: v.discount_type,
  discount_value: Number(v.discount_value),
  min_price: v.min_price === '' ? 0 : Number(v.min_price),
  max_discount: v.max_discount === '' ? null : Number(v.max_discount),
  start_date: toApiDateTime(v.start_date),
  end_date: toApiDateTime(v.end_date),
  usage_limit: v.usage_limit === '' ? 0 : Number(v.usage_limit),
  per_user_usage_count: v.per_user_usage_count === '' ? 0 : Number(v.per_user_usage_count),
  is_active: v.is_active ? 1 : 0,
  modified_by: modifiedBy,
});

// Pull a human message + field-error strings out of a thrown gateway error.
export const extractApiError = (err: any): { message: string; errors: string[] } => {
  const raw = err?.response?.data;
  const env = raw && raw.data && raw.data.status !== undefined ? raw.data : raw;
  const errors = Array.isArray(env?.data)
    ? env.data.filter((x: unknown) => typeof x === 'string')
    : [];
  const message = env?.message || err?.message || 'Something went wrong';
  return { message, errors };
};
